/**
 * SearchesImport — the migrated nuadvisory `SearchImportAdmin` Razor page,
 * recreated as the content-only React route at `/operations/searches-import`.
 *
 * Behaviour: upload a closed-Searches CSV; the nuadvisory .NET API parses,
 * validates headers, and full-replaces the closed-search catalog. The page
 * mirrors the legacy upload form's success/error feedback.
 *
 * API contract (nu PR #67 — `POST /api/Searches/import`):
 *   multipart/form-data, single CSV under the field name `formFile`.
 *   200 -> { updated, inserted }
 *   400 -> { error, missingHeaders: string[] }
 * Called cross-origin with `credentials: "include"` so the `nu_assist_session`
 * cookie rides (the controller's NuAssistOrCookie policy accepts it); no Bearer
 * / antiforgery token. API base URL: VITE_NUADVISORY_API_URL env var.
 *
 * Content-only page: the authenticated shell is provided by `WebShellLayout`.
 */
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, FileCheck2, FileUp, Loader2 } from "lucide-react";
import { Button, ProgressBar } from "@nu-assist/ui";
import { useMe } from "@nu-assist/shared-fe";
import { useAdvisoryFetch, type FetchLike } from "../../context/AdvisoryClientContext";
import { NUADVISORY_BASE } from "../../lib/advisory";
import { ConfirmDialog } from "../../components/ConfirmDialog";

// Client-side guard so an obviously-too-big file is rejected before the upload
// instead of round-tripping to the backend's 413. Generous headroom for a
// closed-Searches CSV (tens of thousands of rows are well under this).
const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface SearchImportResponse {
  updated: number;
  inserted: number;
}

interface SearchImportErrorBody {
  error?: string;
  missingHeaders?: string[];
}

/** Carries the API's structured 400 detail (message + any missing headers). */
class SearchImportError extends Error {
  readonly missingHeaders: string[];
  constructor(message: string, missingHeaders: string[]) {
    super(message);
    this.name = "SearchImportError";
    this.missingHeaders = missingHeaders;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

async function importSearches(file: File, signal?: AbortSignal, fetchImpl: FetchLike = fetch): Promise<SearchImportResponse> {
  const body = new FormData();
  body.append("formFile", file);

  // No Content-Type header — the browser sets the multipart boundary.
  let res: Response;
  try {
    res = await fetchImpl(`${NUADVISORY_BASE}/api/Searches/import`, {
      method: "POST",
      credentials: "include",
      body,
      signal,
    });
  } catch (err) {
    // An abort (a reselect/unmount cancels the in-flight upload) rejects with
    // an AbortError — re-throw it so the caller can ignore it rather than show
    // the network banner below.
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    // Network/CORS failure (offline, preflight blocked, service down) —
    // fetch rejects with a raw "Failed to fetch"; surface something readable.
    throw new SearchImportError(
      "Couldn't reach the import service. Check your connection and try again.",
      [],
    );
  }

  if (!res.ok) {
    let detail: SearchImportErrorBody = {};
    try {
      detail = (await res.json()) as SearchImportErrorBody;
    } catch {
      // Non-JSON error body (e.g. a 413 from the size cap) — fall back to status.
    }
    throw new SearchImportError(
      detail.error ?? `Import failed (${res.status})`,
      detail.missingHeaders ?? [],
    );
  }

  return (await res.json()) as SearchImportResponse;
}

export function SearchesImport() {
  const me = useMe();
  const advisoryFetch = useAdvisoryFetch();
  const inputRef = useRef<HTMLInputElement>(null);
  // Aborts the in-flight upload when a new file is picked (a late response from
  // the abandoned request would otherwise still resolve into the banner).
  const abortRef = useRef<AbortController | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const importMutation = useMutation<SearchImportResponse, SearchImportError, File>({
    mutationFn: (picked) => {
      abortRef.current = new AbortController();
      return importSearches(picked, abortRef.current.signal, advisoryFetch);
    },
    // No toast on settle: the result shows in the inline <output>/alert banner
    // below — a single, persistent channel, not duplicated as a toast. Aborts
    // reject with an AbortError, which the banner's `instanceof` guard skips.
    onError: () => {},
  });

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  const selectFile = (picked: File | null) => {
    if (picked && picked.size > MAX_FILE_BYTES) {
      toast.error(
        `That file is ${formatBytes(picked.size)}. The limit is ${formatBytes(MAX_FILE_BYTES)}.`,
      );
      return;
    }
    abortRef.current?.abort();
    setFile(picked);
    importMutation.reset();
  };

  const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0] ?? null);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const dropped = event.dataTransfer.files?.[0];
    if (!dropped) return;
    // Mirror the picker's accept=".csv" — the input filters by extension but a
    // drop bypasses it, so guard here rather than waiting for the API to reject.
    if (!dropped.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please drop a .csv file.");
      return;
    }
    selectFile(dropped);
  };

  // The import full-replaces the catalog (irreversible), so gate it behind a
  // confirmation rather than firing on the first click.
  const handleImport = () => {
    if (file) setConfirmOpen(true);
  };

  const handleConfirmImport = () => {
    setConfirmOpen(false);
    if (file) importMutation.mutate(file);
  };

  return (
    <div data-testid="searches-import" className="p-12">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <nav aria-label="Breadcrumb" className="mb-2 text-caption text-text-secondary">
          Operations <span className="px-1">/</span>
          <span className="text-accent">Searches Import</span>
        </nav>
        <h1 className="mb-2 text-h1 text-text">Searches Import</h1>
        <p className="mb-6 text-body text-text-secondary">
          Upload the closed-Searches CSV to refresh the searches catalog.
        </p>

        <div className="flex flex-col gap-5">
          {/* What it does */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-2 text-h3 text-text">What it does</h2>
            <p className="text-body text-text-secondary">
              Importing parses the file, validates the expected column headers, and{" "}
              <strong className="font-semibold text-text">fully replaces</strong> the existing
              closed-Searches catalog with the file's rows. An empty file or one missing required
              headers is rejected and nothing is changed.
            </p>
          </section>

          {/* Upload */}
          <section className="rounded-md border border-border bg-surface p-5">
            <h2 className="mb-3 text-h3 text-text">Upload CSV</h2>

            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              onChange={handlePick}
              className="sr-only"
              data-testid="searches-import-file"
            />

            {/* Drop zone — drag a CSV in, or click to browse. */}
            <div
              data-testid="searches-import-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                isDragging ? "border-accent bg-accent-muted" : "border-border-hover bg-surface"
              }`}
            >
              <p className="mb-3 text-caption text-text-secondary">
                Drag &amp; drop a CSV here, or
              </p>
              <span className="inline-flex">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => inputRef.current?.click()}
                >
                  <FileUp size={15} aria-hidden />
                  Choose CSV file
                </Button>
              </span>
              {file ? (
                <div
                  data-testid="searches-import-filename"
                  className="mx-auto mt-4 inline-flex max-w-full items-center gap-2 rounded-md border border-accent bg-accent-muted px-3 py-2"
                >
                  <FileCheck2 size={16} className="shrink-0 text-accent" aria-hidden />
                  <span className="truncate text-label font-semibold text-text">{file.name}</span>
                  <span className="shrink-0 text-caption text-text-secondary">
                    {formatBytes(file.size)}
                  </span>
                </div>
              ) : (
                <p
                  className="mt-3 text-caption text-text-secondary"
                  data-testid="searches-import-filename"
                >
                  No file selected
                </p>
              )}
            </div>

            <div className="mt-4 inline-flex">
              <Button
                type="button"
                onClick={handleImport}
                disabled={!file || importMutation.isPending}
                data-testid="searches-import-submit"
              >
                {importMutation.isPending ? (
                  <>
                    <Loader2 size={15} className="animate-spin" aria-hidden />
                    Importing…
                  </>
                ) : (
                  "Import"
                )}
              </Button>
            </div>
            {/* The button spinner says the CLICK is in flight; the bar says the
                upload is. Indeterminate: the request reports no fraction. */}
            {importMutation.isPending && (
              <ProgressBar indeterminate live label="Importing searches" className="mt-3" />
            )}

            {/* Result — success */}
            {importMutation.isSuccess && (
              <output
                data-testid="searches-import-success"
                className="mt-4 flex items-start gap-2 rounded-md border border-success bg-surface p-4"
              >
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success" aria-hidden />
                <p className="text-body text-text">
                  Import complete —{" "}
                  <strong className="font-semibold">{importMutation.data.inserted}</strong>{" "}
                  searches imported.
                </p>
              </output>
            )}

            {/* Result — error. Guard on the concrete type so an aborted upload
                (which rejects with a DOMException AbortError, not a
                SearchImportError) never renders a bogus banner. */}
            {importMutation.isError && importMutation.error instanceof SearchImportError && (
              <div
                role="alert"
                data-testid="searches-import-error"
                className="mt-4 rounded-md border border-error-strong bg-error-muted p-4"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5 shrink-0 text-error" aria-hidden />
                  <p className="text-body text-error">{importMutation.error.message}</p>
                </div>
                {importMutation.error.missingHeaders.length > 0 && (
                  <ul className="mt-2 list-disc space-y-0.5 pl-8 text-caption text-error">
                    {importMutation.error.missingHeaders.map((header) => (
                      <li key={header}>{header}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Replace the searches catalog?"
        message="Importing fully replaces the existing closed-Searches catalog with this file's rows. This can't be undone."
        confirmLabel="Replace catalog"
        destructive
        busy={importMutation.isPending}
        onConfirm={handleConfirmImport}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
